import { createContextAwareHook } from './useContextAwareHook';
import { useContextAwareGetModuleTypes } from './useGenericOperationsHooks';
import {
    useGetActivitiesByIdQuery,
    usePublicGetActivitiesByIdQuery,
    useGetModulesByActivityIdQuery,
    usePublicGetModulesByActivityIdQuery,
    useLazyGetModulesByActivityIdQuery,
    useLazyPublicGetModulesByActivityIdQuery,
    useGetModulesStatuesQuery,
    usePublicGetModulesStatuesQuery,
    useGetActivityResultsQuery,
    usePublicGetActivityResultsQuery,
    useGetFastActivitiesByProjectQuery,
    usePublicGetFastActivitiesByProjectQuery,
    useLazyGetFastActivitiesByProjectQuery,
    useLazyPublicGetFastActivitiesByProjectQuery,
    useUpdateActivityMutation,
    useCreateActivityMutation,
    useDeleteActivityMutation,
    useCreateActivityCopyMutation,
    useLazyGetActivitiesByIdQuery
} from '../features/activities/activityApiSlice';

/**
 * Returns a hook that automatically selects between public and private APIs
 * for getting an activity by ID based on the current route.
 */
export const useContextAwareGetActivityById = createContextAwareHook(
    useGetActivitiesByIdQuery,
    usePublicGetActivitiesByIdQuery
);

/**
 * Returns a hook that automatically selects between public and private APIs
 * for getting modules by activity ID based on the current route.
 */
export const useContextAwareGetModulesByActivityId = createContextAwareHook(
    useGetModulesByActivityIdQuery,
    usePublicGetModulesByActivityIdQuery
);

/**
 * Returns a hook that automatically selects between public and private APIs
 * for lazy loading modules by activity ID based on the current route.
 */
export const useContextAwareLazyGetModulesByActivityId = createContextAwareHook(
    useLazyGetModulesByActivityIdQuery,
    useLazyPublicGetModulesByActivityIdQuery
);

const useContextAwareGetModulesStatusesQuery = createContextAwareHook(
    useGetModulesStatuesQuery,
    usePublicGetModulesStatuesQuery
);

/**
 * Returns a hook that automatically selects between public and private APIs
 * for getting module statuses based on the current route.
 *
 * getModulesStatues maps module types through the ModuleTypeRegistry in its
 * transformResponse, so a response that lands before /module-types/ would be
 * cached as an empty list. The query is held back until module types (and so the
 * registry) are loaded.
 */
export const useContextAwareGetModulesStatuses: typeof useContextAwareGetModulesStatusesQuery = (activityId, options) => {
    const { data: moduleTypes } = useContextAwareGetModuleTypes({});
    return useContextAwareGetModulesStatusesQuery(activityId, { ...options, skip: options?.skip || !moduleTypes });
};

/**
 * Returns a hook that automatically selects between public and private APIs
 * for getting activity results based on the current route.
 */
export const useContextAwareGetActivityResults = createContextAwareHook(
    useGetActivityResultsQuery,
    usePublicGetActivityResultsQuery
);

/**
 * Returns a hook that automatically selects between public and private APIs
 * for getting fast activities by project based on the current route.
 */
export const useContextAwareGetFastActivitiesByProject = createContextAwareHook(
    useGetFastActivitiesByProjectQuery,
    usePublicGetFastActivitiesByProjectQuery
);

/**
 * Returns a hook that automatically selects between public and private APIs
 * for lazy loading fast activities by project based on the current route.
 */
export const useContextAwareLazyGetFastActivitiesByProject = createContextAwareHook(
    useLazyGetFastActivitiesByProjectQuery,
    useLazyPublicGetFastActivitiesByProjectQuery
); 

export {
    useUpdateActivityMutation,
    useCreateActivityMutation,
    useDeleteActivityMutation,
    useCreateActivityCopyMutation,
    useLazyGetActivitiesByIdQuery
};